import {
  createIamClient,
  DeleteOperation,
  deleteRequestFactory,
  DownloadOperation,
  FindOperation,
  GetOperation,
  getDownloadRequestFactory,
  getRequestFactory,
  getSimpleRequestFactory,
  PostDownloadOperation,
  postDownloadRequestFactory,
  postRequestFactory,
  putRequestFactory,
  SaveOperation,
  SearchOperation,
  searchRequestFactory
} from '@infuse/api/common';
import { Permission } from '@infuse/api/iam/permission';
import {
  AddPermissionsAction,
  CreateRole,
  ExportRolesAction,
  Role,
  RoleImportReport,
  RoleTransfer,
  UpdateRole
} from '@infuse/iam/role/type';
import { MultiSelectOption } from '@infuse/ui/domain';

const client = createIamClient('/rest/roles');

export interface SetRanking {
  ranking: number;
}

export const rolePermissions = {
  SEARCH: 'iam.search-roles',
  SEARCH_COMPACT: 'iam.search-roles-compact',
  GET: 'iam.get-role',
  CREATE: 'iam.create-role',
  UPDATE: 'iam.update-role',
  DELETE: 'iam.delete-role',
  PROMOTE: 'iam.promote-role',
  DEMOTE: 'iam.demote-role',
  SET_RANKING: 'iam.set-role-ranking',
  SEARCH_PERMISSIONS: 'iam.search-role-permissions',
  ADD_PERMISSIONS: 'iam.add-permissions-to-role',
  EXPORT: 'iam.export-roles',
  EXPORT_MULTIPLE: 'iam.export-multiple-roles',
  IMPORT: 'iam.import-roles',
  IMPORT_MULTIPLE: 'iam.import-multiple-roles'
};

// no pagination or filtering available for this api, so we opt for a simple get all request
export const searchRoles: SearchOperation<Role> = searchRequestFactory<Role>(client, {
  permissionId: rolePermissions.SEARCH
});
export const getRole: FindOperation<Role, string> = getRequestFactory<Role, string>(client, {
  permissionId: rolePermissions.GET
});

export const getAllRoleSelectOptions: GetOperation<MultiSelectOption[]> = getSimpleRequestFactory<MultiSelectOption[]>(
  client,
  { path: '/select-options', permissionId: rolePermissions.SEARCH_COMPACT }
);
export const createRole: SaveOperation<CreateRole, never> = postRequestFactory<CreateRole, never>(client, {
  pattern: '',
  permissionId: rolePermissions.CREATE
});
export const updateRole: SaveOperation<UpdateRole, never> = putRequestFactory<UpdateRole, never>(client, {
  pattern: '/{roleId}',
  permissionId: rolePermissions.UPDATE
});
export const deleteRole: DeleteOperation<never> = deleteRequestFactory<never>(client, {
  pattern: '/{roleId}',
  permissionId: rolePermissions.DELETE
});
export const promoteRole: SaveOperation<undefined, never> = postRequestFactory<undefined, never>(client, {
  pattern: '/{roleId}/promote',
  permissionId: rolePermissions.PROMOTE
});
export const demoteRole: SaveOperation<undefined, never> = postRequestFactory<undefined, never>(client, {
  pattern: '/{roleId}/demote',
  permissionId: rolePermissions.DEMOTE
});
export const setRoleRanking: SaveOperation<SetRanking, never> = postRequestFactory<SetRanking, never>(client, {
  pattern: '/{roleId}/set-ranking',
  permissionId: rolePermissions.SET_RANKING
});

export const searchRolePermissions: SearchOperation<Permission> = searchRequestFactory<Permission>(client, {
  pattern: '/{roleId}/permissions',
  permissionId: rolePermissions.SEARCH_PERMISSIONS
});
export const addPermissionsToRole: SaveOperation<AddPermissionsAction, never> = postRequestFactory<
  AddPermissionsAction,
  never
>(client, { pattern: '/{roleId}/add-permissions', permissionId: rolePermissions.ADD_PERMISSIONS });

// Export / import. The export endpoints serve a JSON file, so they are read as a blob in order to
// keep the `Content-Disposition` file name the server picked.
export const exportRole: DownloadOperation<{ id?: string; roleId: string }> = getDownloadRequestFactory<{
  id?: string;
  roleId: string;
}>(client, {
  pattern: '/{roleId}/export',
  permissionId: rolePermissions.EXPORT,
  accept: 'application/json'
});

export const exportRoles: PostDownloadOperation<{ id?: string }, ExportRolesAction> = postDownloadRequestFactory<
  { id?: string },
  ExportRolesAction
>(client, { pattern: '/export', permissionId: rolePermissions.EXPORT, accept: 'application/json' });

export const previewRoleImport: SaveOperation<RoleTransfer[], RoleImportReport> = postRequestFactory<
  RoleTransfer[],
  RoleImportReport
>(client, { pattern: '/import-preview', permissionId: rolePermissions.IMPORT });

export const importRoles: SaveOperation<RoleTransfer[], RoleImportReport> = postRequestFactory<
  RoleTransfer[],
  RoleImportReport
>(client, { pattern: '/import', permissionId: rolePermissions.IMPORT });
